import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import './DemosPage.css';

const DemosPage: React.FC = () => {
  const demos = [
    {
      id: 'rag-simple',
      title: 'RAG Simple',
      description: 'Basic single-domain retrieval for company policy questions. Demonstrates fundamental RAG pipeline with straightforward Q&A.',
      level: 'Foundation',
    },
    {
      id: 'rag-advanced',
      title: 'RAG Advanced',
      description: 'Cross-domain compliance analysis comparing company policies against government regulations. Multi-step retrieval with gap detection and recommendations.',
      level: 'Advanced',
    },
    {
      id: 'orchestrator',
      title: 'Orchestrator Agent',
      description: 'Single agent using multiple tools for document compliance review. Upload a document and see agent reasoning plus tool execution in action.',
      level: 'Enterprise',
    }
  ];

  return (
    <>
      <div className="demos-page">
        <div className="demos-container">
          <Link to="/" className="back-link">← Back to Home</Link>

          <header className="demos-header">
            <h1>Compliance Intelligence System</h1>
            <p className="demos-description">
              A multi-knowledge-base RAG architecture demonstrating how AI can automate compliance monitoring 
              by analyzing alignment between company policies and government regulations.
            </p>
          </header>

          <section className="system-overview">
            <h2>System Overview</h2>
            <div className="overview-content">
              <div className="knowledge-bases">
                <div className="kb-card">
                  <h3>Company Policies</h3>
                  <p>Internal documentation including HR policies, engineering procedures, data privacy guidelines, and financial procedures.</p>
                </div>
                <div className="kb-card">
                  <h3>Government Regulations</h3>
                  <p>Federal Reserve announcements, SEC rules, Treasury regulations, and other financial policy updates.</p>
                </div>
              </div>
              <div className="value-prop">
                <h3>The Problem We Solve</h3>
                <p>
                  When regulations change, companies spend weeks manually reviewing policies for compliance gaps. 
                  This system automates preliminary analysis, flagging conflicts and generating recommendations in minutes.
                </p>
              </div>
            </div>
          </section>

          <section className="demos-list">
            <h2>Three-Tier Demo Architecture</h2>
            <p className="demos-subtitle">
              Each demo builds on the previous, showcasing progression from basic RAG to enterprise-grade compliance automation.
            </p>
            
            <div className="demo-cards">
              {demos.map((demo, index) => (
                <Link
                  key={demo.id}
                  to={`/demo/${demo.id}`}
                  className="demo-card"
                >
                  <div className="demo-card-header">
                    <span className="demo-level">{demo.level}</span>
                  </div>
                  <h3 className="demo-card-title">
                    <span className="demo-number">{index + 1}.</span> {demo.title}
                  </h3>
                  <p className="demo-card-description">{demo.description}</p>
                  <span className="demo-card-cta">Explore Demo →</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="architecture-note">
            <h2>Domain-Agnostic Architecture</h2>
            <p>
              While these demos focus on compliance, the underlying architecture applies to any enterprise knowledge domain—
              regulatory monitoring, internal documentation search, legal contract analysis, or medical research. 
              The system adapts chunking strategy, retrieval approach, and quality checks based on content type and accuracy requirements.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DemosPage;
